import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useToast } from '../components/ToastProvider';
import { api } from '../services/api';
import type { PaymentDetail } from '../services/types';
import { notifyApiResult } from '../utils/notify';
import { formatDate, formatMoney } from '../utils/format';
import { paymentStatusReasonLabel } from '../utils/paymentStatusReason';
import { useAuth } from '../hooks/useAuth';

const STATUSES = ['pending', 'processed', 'failed', 'refunded', 'cancelled'];

export function PaymentDetailPage() {
  const { paymentId } = useParams<{ paymentId: string }>();
  const { user } = useAuth();
  const toast = useToast();
  const canWrite = user?.role === 'finance' || user?.role === 'superadmin';

  const [payment, setPayment] = useState<PaymentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!paymentId) return;
    (async () => {
      setLoading(true);
      const res = await api.getPayment(decodeURIComponent(paymentId));
      if (res.data) {
        setPayment(res.data);
        setNewStatus(res.data.status);
      } else {
        setError(res.error?.message ?? 'Платёж не найден');
      }
      setLoading(false);
    })();
  }, [paymentId]);

  const updateStatus = async () => {
    if (!paymentId || !newStatus) return;
    setBusy(true);
    const res = await api.updatePaymentStatus(decodeURIComponent(paymentId), newStatus);
    if (notifyApiResult(toast, res, () => `Статус платежа обновлён: ${newStatus}`)) {
      setPayment(res.data!);
    } else if (res.error) {
      setError(res.error.message);
    }
    setBusy(false);
  };

  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>;
  if (error && !payment) return <p style={{ color: 'var(--danger)' }}>{error}</p>;
  if (!payment) return null;

  const grantLooksMissing = (() => {
    if (payment.status !== 'processed' || !payment.user_subscription_until || !payment.days) {
      return false;
    }
    const created = new Date(payment.created_at).getTime();
    const until = new Date(payment.user_subscription_until).getTime();
    if (Number.isNaN(created) || Number.isNaN(until)) return false;
    // Even without stacking, until should be at least ~created+days. If shorter — grant likely missed.
    const minExpected = created + payment.days * 24 * 60 * 60 * 1000 - 36 * 60 * 60 * 1000;
    return until < minExpected;
  })();

  return (
    <div>
      <p style={{ marginTop: 0 }}>
        <Link to="/payments">← Платежи</Link>
      </p>

      <h1 style={{ marginTop: 0 }}>Платёж</h1>

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
      {grantLooksMissing && (
        <p
          style={{
            color: 'var(--danger)',
            background: 'color-mix(in srgb, var(--danger) 12%, transparent)',
            padding: '0.75rem 1rem',
            borderRadius: 8,
          }}
        >
          Похоже, платёж processed, но подписка в профиле не покрывает +{payment.days} дн. с даты
          создания. Начисление могло сорваться после claim — выдай дни вручную в Finance → Grant.
        </p>
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <dl className="detail-grid">
          <dt>ID</dt>
          <dd>
            <code>{payment.id}</code>
          </dd>
          <dt>User</dt>
          <dd>
            <Link to={`/users/${payment.user_id}`}>
              {payment.first_name || payment.username || payment.user_id}
            </Link>
          </dd>
          <dt>Сумма</dt>
          <dd>{formatMoney(payment.amount)}</dd>
          <dt>Период</dt>
          <dd>
            {payment.period} мес · {payment.days} дн
          </dd>
          <dt>Tier</dt>
          <dd>{payment.subscription_tier ?? '—'}</dd>
          <dt>Статус</dt>
          <dd>
            {payment.status}
            {paymentStatusReasonLabel(payment.status_reason) ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                {paymentStatusReasonLabel(payment.status_reason)}
                {payment.status_reason ? (
                  <span style={{ opacity: 0.7 }}> · {payment.status_reason}</span>
                ) : null}
              </div>
            ) : payment.status === 'failed' || payment.status === 'cancelled' ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                Обычно отмена пользователем или таймаут pending (poll/TTL, без webhook’ов). Несколько
                failed у одного user = несколько попыток оплаты.
              </div>
            ) : null}
          </dd>
          <dt>Создан</dt>
          <dd>{formatDate(payment.created_at)}</dd>
          <dt>Payment method</dt>
          <dd>{payment.payment_method_id ?? '—'}</dd>
          <dt>Подписка user сейчас</dt>
          <dd>
            {formatDate(payment.user_subscription_until)}
            {payment.user_subscription_tier ? ` · ${payment.user_subscription_tier}` : ''}
            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
              Живой срок из профиля пользователя, не дата окончания этого платежа.
            </div>
          </dd>
        </dl>
      </div>

      {canWrite && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Изменить статус</h3>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <select className="select" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={updateStatus}>
              Сохранить
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
